import React from 'react';
import styled from 'styled-components';
import { 
  FiClock, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiXCircle,
  FiUsers,
  FiPackage,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';

const SummaryContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  background: white;
  padding: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing[3]};
  
  ${({ variant, theme }) => {
    switch (variant) {
      case 'primary':
        return `background-color: ${theme.colors.primary}15;
                color: ${theme.colors.primary};`;
      case 'success':
        return `background-color: ${theme.colors.success}15;
                color: ${theme.colors.success};`;
      case 'warning':
        return `background-color: ${theme.colors.warning}15;
                color: ${theme.colors.warning};`;
      case 'error':
        return `background-color: ${theme.colors.error}15;
                color: ${theme.colors.error};`;
      case 'info':
        return `background-color: ${theme.colors.info}15;
                color: ${theme.colors.info};`;
      default:
        return `background-color: ${theme.colors.primary}15;
                color: ${theme.colors.primary};`;
    }
  }}
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const CardValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing[2]};
`;

const ChangeIndicator = styled.span`
  display: flex;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing[1]};
  
  ${({ isPositive, isNeutral, theme }) => {
    if (isNeutral) {
      return `color: ${theme.colors.textSecondary};`;
    }
    return isPositive 
      ? `color: ${theme.colors.success};` 
      : `color: ${theme.colors.error};`;
  }}
  
  svg {
    margin-right: 2px;
  }
`;

const FooterText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ReportSummary = ({ summary }) => {
  if (!summary) {
    return <div>Loading summary...</div>;
  }

  const totalHours = summary.totalHours ?? 0;
  const approvedHours = summary.approvedHours ?? 0;
  const pendingHours = summary.pendingHours ?? 0;
  const rejectedHours = summary.rejectedHours ?? 0;
  const averageHoursPerEmployee = summary.averageHoursPerEmployee ?? 0;
  const averageHoursPerCustomer = summary.averageHoursPerCustomer ?? 0;
  const currentMonthHours = summary.currentMonthHours ?? 0;
  const previousMonthHours = summary.previousMonthHours ?? 0;
  const totalEntries = summary.totalEntries ?? 0;
  const changePercentage = summary.changePercentage ?? 0;
  const isChangePositive = changePercentage > 0;

  // Guard against divide-by-zero: no hours means 0%
  const percentOfTotal = (part) =>
    totalHours > 0 ? ((part / totalHours) * 100).toFixed(1) : '0.0';

  return (
    <SummaryContainer>
      <SummaryGrid>
        <SummaryCard>
          <CardHeader>
            <IconContainer variant="primary">
              <FiClock />
            </IconContainer>
            <CardTitle>Total Hours</CardTitle>
          </CardHeader>
          <CardValue>{totalHours.toLocaleString()}</CardValue>
          <CardFooter>
            <ChangeIndicator isPositive={isChangePositive}>
              {isChangePositive ? <FiArrowUp /> : <FiArrowDown />}
              {Math.abs(changePercentage)}%
            </ChangeIndicator>
            <FooterText>vs. last month</FooterText>
          </CardFooter>
        </SummaryCard>
        
        <SummaryCard>
          <CardHeader>
            <IconContainer variant="success">
              <FiCheckCircle />
            </IconContainer>
            <CardTitle>Approved Hours</CardTitle>
          </CardHeader>
          <CardValue>{approvedHours.toLocaleString()}</CardValue>
          <CardFooter>
            <ChangeIndicator isNeutral>
              {percentOfTotal(approvedHours)}%
            </ChangeIndicator>
            <FooterText>of total hours</FooterText>
          </CardFooter>
        </SummaryCard>
        
        <SummaryCard>
          <CardHeader>
            <IconContainer variant="warning">
              <FiAlertCircle />
            </IconContainer>
            <CardTitle>Pending Hours</CardTitle>
          </CardHeader>
          <CardValue>{pendingHours.toLocaleString()}</CardValue>
          <CardFooter>
            <ChangeIndicator isNeutral>
              {percentOfTotal(pendingHours)}%
            </ChangeIndicator>
            <FooterText>of total hours</FooterText>
          </CardFooter>
        </SummaryCard>
        
        <SummaryCard>
          <CardHeader>
            <IconContainer variant="error">
              <FiXCircle />
            </IconContainer>
            <CardTitle>Rejected Hours</CardTitle>
          </CardHeader>
          <CardValue>{rejectedHours.toLocaleString()}</CardValue>
          <CardFooter>
            <ChangeIndicator isNeutral>
              {percentOfTotal(rejectedHours)}%
            </ChangeIndicator>
            <FooterText>of total hours</FooterText>
          </CardFooter>
        </SummaryCard>
        
        <SummaryCard>
          <CardHeader>
            <IconContainer variant="info">
              <FiUsers />
            </IconContainer>
            <CardTitle>Avg. Hours per Employee</CardTitle>
          </CardHeader>
          <CardValue>{averageHoursPerEmployee.toLocaleString()}</CardValue>
          <CardFooter>
            <FooterText>Based on active employees</FooterText>
          </CardFooter>
        </SummaryCard>
        
        <SummaryCard>
          <CardHeader>
            <IconContainer variant="info">
              <FiPackage />
            </IconContainer>
            <CardTitle>Avg. Hours per Customer</CardTitle>
          </CardHeader>
          <CardValue>{averageHoursPerCustomer.toLocaleString()}</CardValue>
          <CardFooter>
            <FooterText>Based on active customers</FooterText>
          </CardFooter>
        </SummaryCard>
        
        <SummaryCard>
          <CardHeader>
            <IconContainer variant="primary">
              <FiClock />
            </IconContainer>
            <CardTitle>Current Month Hours</CardTitle>
          </CardHeader>
          <CardValue>{currentMonthHours.toLocaleString()}</CardValue>
          <CardFooter>
            <ChangeIndicator isPositive={isChangePositive}>
              {isChangePositive ? <FiArrowUp /> : <FiArrowDown />}
              {Math.abs(changePercentage)}%
            </ChangeIndicator>
            <FooterText>vs. previous month ({previousMonthHours})</FooterText>
          </CardFooter>
        </SummaryCard>
        
        <SummaryCard>
          <CardHeader>
            <IconContainer variant="primary">
              <FiClock />
            </IconContainer>
            <CardTitle>Total Entries</CardTitle>
          </CardHeader>
          <CardValue>{totalEntries.toLocaleString()}</CardValue>
          <CardFooter>
            <FooterText>Across all employees</FooterText>
          </CardFooter>
        </SummaryCard>
      </SummaryGrid>
    </SummaryContainer>
  );
};

export default ReportSummary;